import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

/** Lightweight onboarding tour. 5 steps, persisted dismissal in
 * localStorage. Can be re-launched any time via window event
 * "axis:start-tour" (Settings toggle uses this).
 */
const STORAGE_KEY = "axis_tour_dismissed_v1";

const STEPS = [
  {
    title: "Welcome to AXISINDIA RFP Analyser",
    body: "This is a 30-second tour. You can skip anytime, and re-launch from Settings.",
    cta: "Start tour",
    next_path: "/v2/dashboard",
  },
  {
    title: "Dashboard — your morning briefing",
    body: "Eight KPIs at a glance: open projects, pipeline value, deadlines, your pending approvals, your checklist, win-rate. Click any tile to drill in.",
    cta: "Next",
    next_path: "/v2/projects",
  },
  {
    title: "Projects (Spaces)",
    body: "Each RFP / RFQ becomes a project. Tag it, set importance, assign a project lead, attach teams. Click into one to start the workflow.",
    cta: "Next",
    next_path: "/v2/projects",
  },
  {
    title: "Inside a project",
    body: "7 tabs: RFP Analysis (upload + 9-category roll-up), Checklist (auto-generated), BOQ (auto-matched to your catalog), Win probability, Quotations (with GST split), GeM stage, Members.",
    cta: "Next",
    next_path: "/v2/chat",
  },
  {
    title: "Ask RFPs — RAG chat",
    body: "Once analysed, ask anything: 'Which RFPs need NABL?' 'What's the EMD for JJM?'. Answers come grounded with page-level citations from the actual tender body.",
    cta: "Got it",
    next_path: null,
  },
];

export default function OnboardingTour() {
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY) === "true";
    if (!dismissed) setStep(0);
    const handler = () => setStep(0);  // external trigger from Settings
    window.addEventListener("axis:start-tour", handler);
    return () => window.removeEventListener("axis:start-tour", handler);
  }, []);

  if (step < 0 || step >= STEPS.length) return null;
  const cur = STEPS[step];

  const next = () => {
    if (cur.next_path) navigate(cur.next_path);
    if (step + 1 >= STEPS.length) {
      localStorage.setItem(STORAGE_KEY, "true");
      setStep(-1);
    } else {
      setStep(step + 1);
    }
  };
  const skip = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setStep(-1);
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 max-w-sm bg-white border border-slate-200 rounded-lg shadow-2xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-blue-600 font-medium">
            Step {step + 1} / {STEPS.length}
          </p>
          <h3 className="text-base font-semibold text-slate-900 mt-1">{cur.title}</h3>
          <p className="text-sm text-slate-600 mt-1">{cur.body}</p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={next}
              className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1">
              {cur.cta} <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button onClick={skip}
              className="px-3 py-1.5 rounded-md text-slate-500 hover:text-slate-700 text-sm">
              Skip
            </button>
          </div>
        </div>
        <button onClick={skip} className="text-slate-300 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 flex gap-1">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-blue-500" : "bg-slate-200"}`} />
        ))}
      </div>
    </div>
  );
}

export function OnboardingToggle() {
  const dismissed = localStorage.getItem(STORAGE_KEY) === "true";
  return (
    <button
      type="button"
      onClick={() => {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent("axis:start-tour"));
      }}
      className="px-3 py-1.5 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
    >
      {dismissed ? "Restart tour" : "Start tour"}
    </button>
  );
}
